// useDerivedSales.js
import { useQuery } from 'react-query';
import useHousePrices from './useHousePrices';

export function useDerivedSales({ validatedAddress }) {
  const postcode = validatedAddress?.postcode;
  const { data: housePricesData, isLoading: isLoadingHousePriceData } = useHousePrices(postcode);
  const sales = housePricesData?.sales ?? [];

  return useQuery(
    ['derivedSales', validatedAddress],
    () => {
      if (!Array.isArray(sales) || sales.length === 0) {
        return {
          currentPropertySales: [],
          areaSales: [],
          propertyAddress: 'Property',
        };
      }

      let currentPropertySales = [];
      let areaSales = [];
      let propertyAddress = 'Property';

      try {
        // If the validated address is detailed enough, use it for filtering.
        if (validatedAddress && validatedAddress.street_number && validatedAddress.street) {
          const streetNumber = String(validatedAddress.street_number).trim();
          const validatedStreet = validatedAddress.street.trim().toLowerCase();
          const flatNumber = validatedAddress.flat_number
            ? validatedAddress.flat_number
                .replace(/^flat\s+/i, '')
                .trim()
                .toLowerCase()
            : null;

          // Find sales that match the current property address.
          currentPropertySales = sales.filter((sale) => {
            const saleHouseNumber = String(sale.address?.paon || '').trim();
            const saleStreet = String(sale.address?.street || '')
              .trim()
              .toLowerCase();
            const saleFlatNumber = sale.address?.saon
              ? String(sale.address.saon)
                  .replace(/^flat\s+/i, '')
                  .trim()
                  .toLowerCase()
              : null;
            const isPAONMatch = saleHouseNumber === streetNumber;
            const isStreetMatch =
              saleStreet.includes(validatedStreet) || validatedStreet.includes(saleStreet);
            const basicMatch = isPAONMatch && isStreetMatch;
            return flatNumber ? basicMatch && saleFlatNumber === flatNumber : basicMatch;
          });

          if (currentPropertySales.length > 0) {
            // Other sales (excluding current property)
            areaSales = sales.filter((sale) => {
              const saleHouseNumber = String(sale.address?.paon || '').trim();
              const saleStreet = String(sale.address?.street || '')
                .trim()
                .toLowerCase();
              const saleFlatNumber = sale.address?.saon
                ? String(sale.address.saon)
                    .replace(/^flat\s+/i, '')
                    .trim()
                    .toLowerCase()
                : null;
              const basicMatch =
                saleHouseNumber === streetNumber &&
                (saleStreet.includes(validatedStreet) || validatedStreet.includes(saleStreet));
              return flatNumber ? !(basicMatch && saleFlatNumber === flatNumber) : !basicMatch;
            });
            propertyAddress = `${validatedAddress.street_number} ${validatedAddress.street}`.trim();
          } else {
            // No matching property sales means treat all sales as area sales.
            areaSales = sales;
            propertyAddress = validatedAddress.street.trim();
          }
        } else {
          // Fallback: if validated address info is missing, use the most recent sale as proxy.
          const sortedSales = [...sales].sort((a, b) => new Date(b.date) - new Date(a.date));
          const recentSale = sortedSales[0];
          const currentPaon = recentSale?.address?.paon;
          const currentStreet = recentSale?.address?.street;
          if (currentPaon && currentStreet) {
            const paonStr = String(currentPaon).trim();
            const streetStr = String(currentStreet).trim().toLowerCase();
            currentPropertySales = sales.filter((sale) => {
              const salePaon = String(sale.address?.paon || '').trim();
              const saleStreet = String(sale.address?.street || '')
                .trim()
                .toLowerCase();
              return (
                salePaon === paonStr &&
                (saleStreet.includes(streetStr) || streetStr.includes(saleStreet))
              );
            });
            areaSales = sales.filter((sale) => {
              const salePaon = String(sale.address?.paon || '').trim();
              const saleStreet = String(sale.address?.street || '')
                .trim()
                .toLowerCase();
              return !(
                salePaon === paonStr &&
                (saleStreet.includes(streetStr) || streetStr.includes(saleStreet))
              );
            });
            propertyAddress = `${recentSale.address.paon || ''} ${
              recentSale.address.street || ''
            }`.trim();
          } else {
            areaSales = sales;
            propertyAddress = 'Property';
          }
        }
      } catch (error) {
        return {
          currentPropertySales: [],
          areaSales: sales,
          propertyAddress: 'Property',
        };
      }

      return { currentPropertySales, areaSales, propertyAddress };
    },
    {
      enabled: !isLoadingHousePriceData,
      staleTime: Infinity,
    },
  );
}
